import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  ColorPalette,
  ColorSwatch,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Input,
  NO_COLOR,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  SELECTOR_VISUAL_RAIL_PX,
  Separator,
  Spinner,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useEntitySelector,
  type EntitySelectorVariant,
  type EntityItemConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type Omittable,
} from "@salestrak/ui";

import { usePermission } from "#app/lib/hooks/use-permission";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import {
  NONE_OPTION_LABEL,
  NONE_OPTION_VALUE,
} from "#app/lib/custom-field-utils";
import type { CustomFieldDef } from "./types";
import {
  getOptionMetas,
  OptionChip,
  randomOptionColor,
  showsOptionColors,
  type OptionColorChoice,
  type OptionMeta,
} from "./option-visuals";

// ---------------------------------------------------------------------------
// OptionSelector — THE value picker for the option field types (Single /
// Multi Select): searchable option rows with their color swatches, and an
// inline "Create <name>" row. The caller owns the trigger (chips, a table
// cell, a form control) exactly like every other EntitySelector surface.
//
// Multi Select is a token field: the applied values stand in the search well
// as their own chips, each with an ×, and Backspace on an empty query pops the
// last one. Enter picks the painted match while one exists; with nothing
// matching it creates what was typed. The create row previews the exact chip
// the option becomes — tinted for Multi Select, solid for Single Select.
//
// The create row is triple-gated at the affordance, so no host can offer a
// row the server rejects: an onCreateOption handler must be wired, the
// viewer needs the create.items grant (the server's own gate), and the
// field's allowOptionCreate config (absent means true) must permit it.
// ---------------------------------------------------------------------------

export type OnCreateOption = (
  customFieldId: string,
  label: string,
  color: string,
) => Promise<unknown>;

export type OptionSelectorProps = Omittable<{
  field: CustomFieldDef;
  /** Selected option values (always the array shape, single included). */
  values: string[];
  /** Multi keeps the popup open and toggles; single replaces and closes. */
  multiple: boolean;
  onValuesChange: (next: string[]) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreateOption?: OnCreateOption;
  /** Field managers only (hosts gate on custom_fields.manage): in-picker
   *  option management — a hover "…" editor per row (rename, recolor,
   *  two-step delete) and drag reorder under manual sort. Deleting an
   *  option that's currently applied also drops it from the value. */
  optionActions?: OptionActions;
  /** The trigger (an `OptionSelectorTrigger`, or a cell body already wearing
   *  one). */
  children: React.ReactNode;
}>;

const OPTIONS_POOL_ID = "options";

// Every row visual sits centered in the selector's 24px rail, so the swatch
// rows, the clear row's empty rail, and the titles all share one content line.
function railBox(children?: React.ReactNode) {
  return (
    <Box
      width={SELECTOR_VISUAL_RAIL_PX}
      height={SELECTOR_VISUAL_RAIL_PX}
      align="center"
      justify="center"
      shrink={false}
    >
      {children}
    </Box>
  );
}

// A row IS the option's chip — the value the pick becomes, tonal for Single
// Select and soft for Multi Select (the reference select lists its values as
// chips, not as dot-and-label rows). A legacy stored "none" color renders the
// chip colorless; the choice is no longer offered (the field-level color
// toggle took its job). `avatar` stays for the surfaces that read the entity's
// visual off the selection (never the row).
//
// A field with its colors off lists its options as plain words: a grey chip
// per row would be a box around every word for nothing (the value grammar's
// "colors off reads as text"), and there is no swatch to show.
function optionItem(meta: OptionMeta, multiple: boolean, colored = true): EntityItemConfig {
  if (!colored) {
    return {
      id: meta.value,
      title: meta.label,
      body: () => (
        <Text size="sm" truncate>
          {meta.label}
        </Text>
      ),
    };
  }
  return {
    id: meta.value,
    title: meta.label,
    body: () => (
      <OptionChip
        label={meta.label}
        color={meta.color}
        tone={multiple ? "soft" : "tonal"}
      />
    ),
    avatar: {
      render: () =>
        railBox(
          meta.color === NO_COLOR ? (
            <Icon name="circle-slash" size="sm" color="muted" />
          ) : (
            <ColorSwatch color={meta.color} size="sm" rounded="full" />
          ),
        ),
    },
  };
}

export function OptionSelector({
  field,
  values,
  multiple,
  onValuesChange,
  open,
  onOpenChange,
  onCreateOption,
  optionActions,
  children,
}: OptionSelectorProps) {
  const metas = useMemo(() => getOptionMetas(field), [field]);
  const colored = showsOptionColors(field);
  const canCreateItems = usePermission("create.items");
  const allowCreate =
    !!onCreateOption &&
    canCreateItems &&
    field.config?.allowOptionCreate !== false;

  // A configured default declares the field is always meant to hold a value,
  // so "no choice" stops being a legal state — the clear row hides.
  const hasConfiguredDefault =
    Array.isArray(field.config?.defaultValue) &&
    field.config.defaultValue.length > 0;

  // Deleting an option that's currently applied also drops it from the
  // value — otherwise the cell keeps a grey orphan chip for a choice that
  // no longer exists. Every OTHER issue holding it keeps the orphan (values
  // are never scrubbed server-side); only the one being edited cleans up.
  const handleDeleteOption = useCallback(
    (value: string) => {
      void optionActions?.deleteOption(field, value);
      if (values.includes(value)) {
        onValuesChange(values.filter((v) => v !== value));
      }
    },
    [optionActions, field, values, onValuesChange],
  );

  const handleUpdateOption = useCallback(
    (value: string, patch: { label?: string; color?: OptionColorChoice }) => {
      void optionActions?.updateOption(field, value, patch);
    },
    [optionActions, field],
  );

  const items = useMemo(() => {
    const rows = metas.map((meta) => {
      const item = optionItem(meta, multiple, colored);
      if (optionActions) {
        item.trailing = () => (
          <OptionRowEditor
            meta={meta}
            onUpdate={handleUpdateOption}
            onDelete={handleDeleteOption}
          />
        );
      }
      return item;
    });
    if (!multiple && !hasConfiguredDefault) {
      // The clear row is synthetic — it is not an option, so it takes no
      // editor and never drags. Its body is the bare dash, inset to the
      // chips' text edge so it reads as the first entry in the list.
      rows.unshift({
        id: NONE_OPTION_VALUE,
        title: NONE_OPTION_LABEL,
        reorderable: false,
        body: () => (
          <Box className={colored ? "ps-1.5" : undefined}>
            <Text size="sm" variant="muted">
              {NONE_OPTION_LABEL}
            </Text>
          </Box>
        ),
      });
    }
    return rows;
  }, [
    colored,
    metas,
    multiple,
    hasConfiguredDefault,
    optionActions,
    handleUpdateOption,
    handleDeleteOption,
  ]);

  // Drag reorder persists the full option list in the dropped order. Manual
  // sort only — the az/za modes own the order, and a reordered list would
  // snap back on the next normalize.
  const sortIsManual = (field.config?.optionSort ?? "manual") === "manual";
  const handleReorder = useCallback(
    (draggedId: string, beforeId: string | null) => {
      const dragged = metas.find((m) => m.value === draggedId);
      if (!dragged) return;
      const without = metas.filter((m) => m.value !== draggedId);
      // A drop above the synthetic clear row means "first option".
      const insertAt =
        beforeId === null
          ? without.length
          : beforeId === NONE_OPTION_VALUE
            ? 0
            : without.findIndex((m) => m.value === beforeId);
      if (insertAt < 0) return;
      void optionActions?.reorderOptions(field, [
        ...without.slice(0, insertAt),
        dragged,
        ...without.slice(insertAt),
      ]);
    },
    [metas, optionActions, field],
  );
  const reorder = !!optionActions && sortIsManual && metas.length > 1;

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        {
          id: OPTIONS_POOL_ID,
          label: "Options",
          items,
          // An authored, ordered list advertises its order: managers see the
          // grip at rest, not on approach.
          ...(reorder
            ? { onReorder: handleReorder, gripVisibility: "always" as const }
            : {}),
        },
      ],
      searchPlaceholder: "Search options...",
      emptyMessage: "No matching options",
      noItemsMessage: "No options yet",
      // Single-select replaces the pick and auto-closes; multi toggles.
      ...(multiple ? {} : { maxSelections: 1 }),
    }),
    [items, multiple, reorder, handleReorder],
  );

  const selections = useMemo<EntitySelection[]>(
    () =>
      values.flatMap((value) => {
        const meta = metas.find((m) => m.value === value);
        // Stored-but-deleted values stay selected (they render as grey
        // fallback chips in the trigger) but have no row to mirror.
        if (!meta) return [];
        return [
          { id: value, poolId: OPTIONS_POOL_ID, entity: optionItem(meta, multiple, colored) },
        ];
      }),
    [values, metas, multiple, colored],
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const ids = next
        .map((s) => s.id)
        .filter((id) => id !== NONE_OPTION_VALUE);
      if (!multiple) {
        onValuesChange(ids.slice(0, 1));
        return;
      }
      // Keep stored-but-deleted values: they had no selection row, so a
      // toggle elsewhere must not silently drop them.
      const known = new Set(metas.map((m) => m.value));
      const unknownKept = values.filter((v) => !known.has(v));
      onValuesChange([...unknownKept, ...ids]);
    },
    [multiple, metas, values, onValuesChange],
  );

  // Whether a typed query is one the create row would offer to mint: allowed,
  // non-empty, and not already an option (case-insensitive).
  const canCreateQuery = useCallback(
    (query: string) => {
      const label = query.trim();
      return (
        allowCreate &&
        !!label &&
        !metas.some((m) => m.label.trim().toLowerCase() === label.toLowerCase())
      );
    },
    [allowCreate, metas],
  );

  const handleCreate = useCallback(
    async (label: string, color: string) => {
      if (!onCreateOption) return;
      await onCreateOption(field.customFieldId, label, color);
      // Apply the new option immediately — creating it from the picker IS
      // choosing it. Its value derives from the label server-side.
      onValuesChange(multiple ? [...values, label] : [label]);
      if (!multiple) onOpenChange?.(false);
    },
    [onCreateOption, field.customFieldId, multiple, values, onValuesChange, onOpenChange],
  );

  return (
    <EntitySelector
      title={field.name}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
      {/* The create state reads the live query, so it mounts INSIDE the
          selector; it hands its answers down as a render prop so the search
          well stays a DIRECT child of the content — the drawer pins its
          header by child type, and a wrapper component would hide it. */}
      <OptionCreateState
        metas={metas}
        canCreateQuery={canCreateQuery}
        onCreate={handleCreate}
      >
        {(createState) => {
          const { variant: selectorVariant, canCreate, create } = createState;
          // The applied values as tokens in the well. Stored-but-deleted
          // values keep the grey fallback chip so they can still be removed
          // from here.
          const tokens =
            multiple && values.length > 0
              ? values.map((value, index) => {
                  const meta = metas.find((m) => m.value === value);
                  return (
                    <OptionChip
                      key={`${value}-${index}`}
                      label={meta?.label ?? value}
                      color={meta?.color ?? "grey"}
                      onDismiss={() =>
                        onValuesChange(values.filter((v) => v !== value))
                      }
                    />
                  );
                })
              : undefined;
          // Desktop: the flush header row (no frame, hairline under) — the
          // search IS the popup's first row, the tokens ride inline with the
          // query. Phone: the framed well every phone sheet's search band
          // wears, pinned in the drawer header above the scrolling rows.
          const drawer = selectorVariant === "drawer";
          return (
            <EntitySelectorContent align="start">
              <EntitySelectorInput
                variant={drawer ? undefined : "header"}
                tinted={!drawer}
                leading={tokens}
                hideIcon
                // With tokens standing in the well, the chips say what the
                // field holds — the placeholder steps aside (the field keeps
                // its name).
                placeholder={
                  tokens
                    ? ""
                    : allowCreate
                      ? "Search or add options..."
                      : undefined
                }
                onBackspaceEmpty={
                  tokens ? () => onValuesChange(values.slice(0, -1)) : undefined
                }
                onSearchSubmit={canCreate ? () => void create() : undefined}
              />
              {/* The selection is stated elsewhere — tokens up top (multi),
                  the value on the trigger (single) — so the rows carry no
                  checkbox, check, or wash; and the search band already names
                  what the rows are, so no "Options" label. */}
              <EntitySelectorPool
                value={OPTIONS_POOL_ID}
                hideLabel
                hideSelectionMarks
                hideSelectionHighlight
              />
              <OptionCreateRow state={createState} multiple={multiple} />
              {/* In the content's own tree (its fallback scan cannot see
                  inside a consumer component). Silent while the create row
                  answers the query. */}
              <EntitySelectorEmpty>
                {({ search }) =>
                  canCreateQuery(search) ? null : "No matching options"
                }
              </EntitySelectorEmpty>
            </EntitySelectorContent>
          );
        }}
      </OptionCreateState>
    </EntitySelector>
  );
}

// ---------------------------------------------------------------------------
// Create state — mounted INSIDE the selector so it can read the live query:
// whether THIS query can be minted, and the one-shot create. The create color
// is pre-rolled per query, non-repeating against the last option, and the
// preview chip IS the chip the option gets.
// ---------------------------------------------------------------------------

type OptionCreate = {
  variant: EntitySelectorVariant;
  label: string;
  color: ReturnType<typeof randomOptionColor>;
  creating: boolean;
  /** The create affordance answers this query. */
  canCreate: boolean;
  /** Create is what Enter does — only once nothing matches. */
  createIsEnterTarget: boolean;
  create: () => Promise<void>;
};

function useOptionCreate({
  metas,
  canCreateQuery,
  onCreate,
}: {
  metas: OptionMeta[];
  canCreateQuery: (query: string) => boolean;
  onCreate: (label: string, color: string) => Promise<void>;
}): OptionCreate {
  const { search, setSearch, getFilteredItems, variant } = useEntitySelector();
  const [creating, setCreating] = useState(false);

  const label = search.trim();
  const canCreate = canCreateQuery(search);
  // The synthetic clear row is not a match.
  const matchCount = getFilteredItems(OPTIONS_POOL_ID).filter(
    (item) => item.id !== NONE_OPTION_VALUE,
  ).length;
  const createIsEnterTarget = canCreate && matchCount === 0;

  // One roll per query text — the swatch must not reshuffle every render.
  const color = useMemo(
    () => randomOptionColor(metas[metas.length - 1]?.color),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-roll per query only
    [label],
  );

  const create = useCallback(async () => {
    if (creating || !canCreate) return;
    setCreating(true);
    try {
      await onCreate(label, color);
      setSearch("");
    } finally {
      setCreating(false);
    }
  }, [creating, canCreate, onCreate, label, color, setSearch]);

  return { variant, label, color, creating, canCreate, createIsEnterTarget, create };
}

function OptionCreateState({
  children,
  ...props
}: Parameters<typeof useOptionCreate>[0] & {
  children: (state: OptionCreate) => React.ReactNode;
}) {
  return <>{children(useOptionCreate(props))}</>;
}

// The create row, visible while the query names an option that doesn't exist
// yet. It takes the ONE create state the well's Enter uses, so the previewed
// chip color and the in-flight guard are the same on both paths.
function OptionCreateRow({
  state,
  multiple,
}: {
  state: OptionCreate;
  multiple: boolean;
}) {
  const { label, color, creating, canCreate, createIsEnterTarget, create } = state;
  if (!canCreate) return null;
  return (
    <Box border="top" padding="xs">
      <Box
        as="button"
        type="button"
        direction="row"
        align="center"
        justify="between"
        gap="sm"
        paddingX="sm"
        minHeight={32}
        rounded="sm"
        fill="width"
        hoverBackground
        aria-label={`Create option ${label}`}
        onClick={() => void create()}
      >
        <Box direction="row" align="center" gap="sm" shrink>
          <Text size="sm" variant="muted">
            Create
          </Text>
          {creating ? (
            <Spinner size="sm" />
          ) : (
            <OptionChip
              label={label}
              color={color}
              tone={multiple ? "soft" : "tonal"}
            />
          )}
        </Box>
        {/* The return glyph marks create as what Enter does — only once
            nothing matches; with matches present Enter picks the first. */}
        {createIsEnterTarget ? (
          <Icon name="corner-down-left" size="sm" color="muted" />
        ) : null}
      </Box>
    </Box>
  );
}

export { EntitySelectorTrigger as OptionSelectorTrigger };

// ---------------------------------------------------------------------------
// Row editor — the hover "…" on an option row (managers only). Rename input
// (blur/Enter commits, value preserved), the curated palette with No color,
// and a two-step delete. The trigger's popup-open state pins the row's
// trailing chrome, so the editor survives the pointer leaving the row.
// ---------------------------------------------------------------------------

function OptionRowEditor({
  meta,
  onUpdate,
  onDelete,
}: {
  meta: OptionMeta;
  onUpdate: (value: string, patch: { label?: string; color?: OptionColorChoice }) => void;
  onDelete: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  shape="square"
                  aria-label={`Settings for ${meta.label}`}
                  // A pointer shortcut inside an option: out of the tab
                  // order. Settings › Custom fields is the keyboard path.
                  tabIndex={-1}
                >
                  <Icon name="ellipsis" size="sm" color="muted" />
                </Button>
              }
            />
          }
        />
        <TooltipPopup side="top">
          <Text size="xs">Settings</Text>
        </TooltipPopup>
      </Tooltip>
      {/* The card rung, not content width: twelve swatches in one row made
          the editor a slab. Two rows of six sit under a name field that has
          room to read. */}
      <PopoverPopup align="start" side="bottom" width="card">
        {open ? (
          <OptionEditContent
            meta={meta}
            onUpdate={onUpdate}
            onDelete={onDelete}
            close={() => setOpen(false)}
          />
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}

function OptionEditContent({
  meta,
  onUpdate,
  onDelete,
  close,
}: {
  meta: OptionMeta;
  onUpdate: (value: string, patch: { label?: string; color?: OptionColorChoice }) => void;
  onDelete: (value: string) => void;
  close: () => void;
}) {
  const [name, setName] = useState(meta.label);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const commitRename = useCallback(() => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== meta.label) {
      onUpdate(meta.value, { label: trimmed });
    }
  }, [name, meta.value, meta.label, onUpdate]);

  return (
    <Box direction="column" gap="sm">
      <Input
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={commitRename}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commitRename();
            close();
          }
        }}
        placeholder="Option name"
        size="sm"
        autoFocus
      />
      <ColorPalette
        columns={6}
        // A legacy "none" value has no swatch to mark; grey stands in until
        // a pick replaces it.
        color={meta.color === NO_COLOR ? "grey" : meta.color}
        onColorChange={(color) => onUpdate(meta.value, { color })}
      />
      <Separator />
      {confirmingDelete ? (
        <Box direction="row" align="center" justify="end" gap="sm">
          <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
            Cancel
          </Button>
          <Button
            color="destructive"
            size="sm"
            onClick={() => {
              onDelete(meta.value);
              close();
            }}
          >
            Delete option
          </Button>
        </Box>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          justify="start"
          onClick={() => setConfirmingDelete(true)}
        >
          <Icon name="trash" size="sm" color="muted" />
          <Text size="sm">
            Delete
          </Text>
        </Button>
      )}
    </Box>
  );
}
